import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Wallet,
  TrendingUp,
  Receipt,
  Plus,
  PiggyBank,
  CreditCard,
  X
} from 'lucide-react';

const BottomNav: React.FC = () => {
  const [fabOpen, setFabOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get context-aware FAB actions based on current page
  const getFabActions = () => {
    const path = location.pathname;

    if (path === '/budget') {
      return [
        { label: 'Add Budget Item', icon: Wallet, action: () => {} } // Will trigger modal
      ];
    }

    if (path === '/net-worth' || path === '/assets' || path === '/liabilities') {
      return [
        { label: 'Add Asset', icon: TrendingUp, action: () => navigate('/assets?add=true') },
        { label: 'Add Liability', icon: CreditCard, action: () => navigate('/liabilities?add=true') }
      ];
    }

    if (path === '/transactions') {
      return [
        { label: 'Add Transaction', icon: Receipt, action: () => {} }
      ];
    }

    // Default (Dashboard)
    return [
      { label: 'Add Transaction', icon: Receipt, action: () => navigate('/transactions?add=true') },
      { label: 'Add Asset', icon: PiggyBank, action: () => navigate('/assets?add=true') }
    ];
  };

  const fabActions = getFabActions();

  const handleFabClick = () => {
    if (fabActions.length === 1) {
      // Single action - execute directly
      fabActions[0].action();
    } else {
      // Multiple actions - toggle menu
      setFabOpen(!fabOpen);
    }
  };

  const handleActionClick = (action: () => void) => {
    action();
    setFabOpen(false);
  };

  // Close FAB menu when clicking outside
  const handleOverlayClick = () => {
    setFabOpen(false);
  };

  return (
    <>
      {/* Overlay to close FAB menu */}
      {fabOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 45,
          }}
          onClick={handleOverlayClick}
        />
      )}

      <nav className="bottom-nav">
        {/* Home */}
        <NavLink
          to="/"
          end
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <Home
            className="nav-icon"
            fill={location.pathname === '/' ? 'currentColor' : 'none'}
            strokeWidth={location.pathname === '/' ? 1.5 : 2}
          />
        </NavLink>

        {/* Budget */}
        <NavLink
          to="/budget"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <Wallet
            className="nav-icon"
            fill={location.pathname === '/budget' ? 'currentColor' : 'none'}
            strokeWidth={location.pathname === '/budget' ? 1.5 : 2}
          />
        </NavLink>

        {/* FAB */}
        <div className="fab-container">
          <div className={`fab-menu ${fabOpen ? 'open' : ''}`}>
            {fabActions.map((action, index) => (
              <button
                key={index}
                className="fab-menu-item"
                onClick={() => handleActionClick(action.action)}
              >
                <action.icon size={18} />
                {action.label}
              </button>
            ))}
          </div>
          <button
            className={`fab ${fabOpen ? 'open' : ''}`}
            onClick={handleFabClick}
            aria-label="Quick add"
          >
            {fabOpen ? (
              <X className="fab-icon" />
            ) : (
              <Plus className="fab-icon" />
            )}
          </button>
        </div>

        {/* Net Worth */}
        <NavLink
          to="/net-worth"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <TrendingUp
            className="nav-icon"
            fill={location.pathname === '/net-worth' ? 'currentColor' : 'none'}
            strokeWidth={location.pathname === '/net-worth' ? 1.5 : 2}
          />
        </NavLink>

        {/* Transactions */}
        <NavLink
          to="/transactions"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <Receipt
            className="nav-icon"
            fill={location.pathname === '/transactions' ? 'currentColor' : 'none'}
            strokeWidth={location.pathname === '/transactions' ? 1.5 : 2}
          />
        </NavLink>
      </nav>
    </>
  );
};

export default BottomNav;
